import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import AjaxErrorBoundary from '../../../ajax/errorBoundary';
import ErrorList from '../../../errorList';
import { FormHeading, ButtonContainer, Well } from '../../../form';
import SiteList from './SiteList';
import {
  ConfirmationModal,
  IConfirmationModalProps,
  INotificationModalProps,
  NotificationModal,
  useModal,
} from '../../../modals';
import { useAuthentication } from '../../../authentiction';
import { useDataFetcher } from '../../../hooks';
import activateSite from './activateSite';
import deactivateSite from './deactivateSite';
import reloadSite from './reloadSite';
import { IApiError } from '../../../../services';
import { ISiteListItem } from './types';

const SiteListPage: React.FC = () => {
  const history = useHistory();
  const { showModal, hideModal } = useModal();

  const { numberOfSites } = useAuthentication();

  const { busy, errors, data } = useDataFetcher('/api/sites');

  const [ajaxErrors, setAjaxErrors] = useState<Array<string>>([]);
  const [sites, setSites] = useState<Array<ISiteListItem>>([]);
  const [showDeactivatedSites, setShowDeactivatedSites] =
    useState<boolean>(false);

  useEffect(() => {
    if (data) {
      setSites(data.sites);
    }
  }, [data]);

  const getFilteredSites = (
    sites: Array<ISiteListItem>,
    showDeactivated: boolean,
  ): Array<ISiteListItem> =>
    showDeactivated ? sites : sites.filter((site) => !site.deactivated);

  const filteredSites = useMemo(
    () => getFilteredSites(sites, showDeactivatedSites),
    [sites, showDeactivatedSites],
  );

  const showLicenseNotification = () => {
    const notificationProps: INotificationModalProps = {
      title: 'Site Limit Reached',
      message: `You are only licensed for ${numberOfSites} site${
        numberOfSites > 1 ? 's' : ''
      }, to add more sites please purchase a new license`,
      icon: ['far', 'info-circle'],
      kind: 'info',
      onRequestClose: () => {
        hideModal();
      },
    };

    showModal(NotificationModal, notificationProps);
  };

  const showSiteReloadedConfirmation = (pin: string) => {
    const notificationProps: INotificationModalProps = {
      title: 'Site Reloaded',
      message: `Site ${pin} has been reloaded`,
      icon: ['far', 'info-circle'],
      kind: 'info',
      onRequestClose: () => {
        hideModal();
      },
    };

    showModal(NotificationModal, notificationProps);
  };

  const handleCreateNewSite = () => {
    if (sites.filter((site) => !site.deactivated).length >= numberOfSites) {
      showLicenseNotification();
    } else {
      history.push('/admin/sites/create');
    }
  };

  const updateDeactivatedFlag = (pin: string, deactivated: boolean) => {
    const index = sites.findIndex((site) => site.pin === pin);

    setSites([
      ...sites.slice(0, index),
      { ...sites[index], deactivated },
      ...sites.slice(index + 1),
    ]);
  };

  const handleActivateSite = (pin: string) => {
    activateSite(pin)
      .then(() => {
        updateDeactivatedFlag(pin, false);
      })
      .catch((error: IApiError) => {
        if (error.errorNumber === 1009) {
          showLicenseNotification();
        } else {
          setAjaxErrors(error.messages);
        }
      });
  };

  const handleDeactivateSite = (pin: string) => {
    const confirmationProps: IConfirmationModalProps = {
      title: 'Deactivate Site',
      message: 'Are you sure that you to deactivate this site?',
      icon: ['far', 'question-circle'],
      kind: 'danger',
      onConfirmed: () => {
        hideModal();

        deactivateSite(pin)
          .then(() => {
            updateDeactivatedFlag(pin, true);
          })
          .catch((error: IApiError) => setAjaxErrors(error.messages));
      },
      onRequestClose: () => hideModal(),
    };

    showModal(ConfirmationModal, confirmationProps);
  };

  const handleReloadSite = (pin: string) => {
    reloadSite(pin)
      .then(() => showSiteReloadedConfirmation(pin))
      .catch((error: IApiError) => setAjaxErrors(error.messages));
  };

  return (
    <Container fluid>
      <Well>
        <FormHeading title="Sites" subTitle="List" backPath="/admin" />
        <ButtonContainer align="left">
          <Button type="button" onClick={handleCreateNewSite}>
            Create a new site
          </Button>
          <Button
            type="button"
            variant="info"
            onClick={() => setShowDeactivatedSites(!showDeactivatedSites)}
          >
            {showDeactivatedSites ? 'Hide' : 'Show'} Deactivated Sites
          </Button>
        </ButtonContainer>
        <AjaxErrorBoundary
          busy={busy}
          loadingText="Loading sites"
          errors={errors}
        >
          <ErrorList errors={ajaxErrors} />
          <SiteList
            sites={filteredSites}
            onActivate={handleActivateSite}
            onDeactivate={handleDeactivateSite}
            onReloadSite={handleReloadSite}
          />
        </AjaxErrorBoundary>
      </Well>
    </Container>
  );
};

export default SiteListPage;
